/* import __COLOCATED_TEMPLATE__ from './search2.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
/* === ⚠️ THIS FILE CURRENTLY USES DEPRECATED PATTERNS ⚠️ === */
/* === 🔗 For more information visit https://go.inter.com/ember-best-practices 🔗 */
/* === 🚀 Please consider refactoring & removing some of the comments below when working on this file 🚀 */
/* eslint-disable @intercom/intercom/no-default-task-ember-concurrency */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { AsyncData } from 'embercom/resources/utils/async-data';
import { use } from 'ember-resources/util/function-resource';
import type Session from 'embercom/services/session';
import type Inbox2AssigneeSearch from 'embercom/services/inbox2-assignee-search';
import type Inbox2TagsSearch from 'embercom/services/inbox2-tags-search';
import { action } from '@ember/object';
import type AdminSummary from 'embercom/objects/inbox/admin-summary';
import { isEmpty } from '@ember/utils';
import type RouterService from '@ember/routing/router-service';
import { isEqual } from 'underscore';
import ENV from 'embercom/config/environment';

// @ts-ignore
import { cached } from 'tracked-toolbox';
import PredicateGroup, {
  type DateRangePredicate,
  type NestedPredicate,
  type SimplePredicate,
  toAbsoluteDate,
  fromAbsoluteDate,
  type BooleanComparison,
  type Comparison,
  type TicketStatePredicate,
} from 'embercom/objects/inbox/search/predicate-group';
import type TeamSummary from 'embercom/objects/inbox/team-summary';
import type Tag from 'embercom/objects/inbox/tag';
import type ConversationTopic from 'embercom/objects/inbox/conversation-topic';
import { restartableTask } from 'ember-concurrency-decorators';
import { all, type TaskGenerator, timeout } from 'ember-concurrency';
import type UserSummary from 'embercom/objects/inbox/user-summary';
import type UserApi from 'embercom/services/user-api';
import type CompanySummary from 'embercom/objects/inbox/company-summary';
import type CompanyApi from 'embercom/services/company-api';
import {
  InboxSearchSort,
  type InboxStateOption,
  isTicketState,
  intToState,
  stateToInt,
  InboxConversationTypeOption,
} from 'embercom/models/data/inbox/inbox-filters';
import type IntlService from 'embercom/services/intl';
import type AdminWithPermissions from 'embercom/objects/inbox/admin-with-permissions';
import type ExperimentsApi from 'embercom/services/experiments-api';
import type moment from 'moment-timezone';
import { type ColumnDefinition } from 'embercom/objects/inbox/conversations-table-column';
import type ConversationsTableColumn from 'embercom/objects/inbox/conversations-table-column';
import {
  ColumnDefinitionsBuilder,
  ConversationsTableColumns,
  columnId,
} from 'embercom/objects/inbox/conversations-table-column';
import { type SortState } from './conversations-table';
import { type SortDirection } from 'embercom/services/inbox-api';
import { type SelectedColumn } from 'embercom/services/conversations-table-settings';
import type ConversationsTableSettings from 'embercom/services/conversations-table-settings';
import { tracked } from '@glimmer/tracking';
import type ConversationalInsightsApi from 'embercom/services/inbox2-conversational-insights-api';
import type ConversationAttributeDescriptor from 'embercom/objects/inbox/conversation-attribute-descriptor';
import { DataType } from 'embercom/objects/inbox/conversation-attribute-descriptor';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
// @ts-ignore
import { registerDestructor } from '@ember/destroyable';
import { type TicketDescriptor, type TicketType } from 'embercom/objects/inbox/ticket';
import type InboxApi from 'embercom/services/inbox-api';
import type InboxState from 'embercom/services/inbox-state';
import type ConversationTableEntry from 'embercom/objects/inbox/conversation-table-entry';
import { REQUIRED_CONVERSATIONS_TABLE_FIELDS } from 'embercom/objects/inbox/conversation-table-entry';
import { type ConversationsSearchResource } from './search/conversations-search-resource';
import type InboxRedirectionService from 'embercom/services/inbox-redirection-service';
import { type TicketSystemState } from 'embercom/objects/inbox/conversation';
import type FilterableAttributesService from 'embercom/services/filterable-attributes';
import {
  type AttributeWithListOptions,
  type TicketDescriptorData,
  type TicketTypeData,
} from 'embercom/services/filterable-attributes';
import type InboxSidebarService from 'embercom/services/inbox-sidebar-service';
import type { Monitor } from 'embercom/services/monitors-api';
import { getValueFromMonitorPairs } from 'embercom/lib/inbox2/qa-monitor-helpers';

interface Args {
  filters?: string;
  query?: string;
  sort?: InboxSearchSort;
  monitor?: string;
  qaMode?: string;
  backfillStatus?: string;
}

interface SortOption {
  id: InboxSearchSort;
  text: string;
  sortState: SortState;
}

export interface MoreFiltersGroup {
  heading?: string;
  items: Array<MoreFiltersItem>;
  id: number;
}

export interface MoreFiltersItem {
  attribute:
    | ConversationAttributeDescriptor
    | TicketDescriptor
    | AttributeWithListOptions
    | TicketDescriptorData;
  value?: string | CvdaDateTimeItem['value'] | number | null;
  comparison?: Comparison;
  groupId: number;
}

export interface CvdaListItem {
  text: string;
  id: string;
}

export interface CvdaDateTimeItem {
  value: {
    value: string;
    unit: 'days' | 'hours' | 'minutes';
    direction: 'past';
  };
  comparison: Comparison;
}

const convertStringIdToNumber = (id?: string | number | null) => {
  return id && typeof id === 'string' ? parseFloat(id) : (id as number | null | undefined);
};

export default class Search2Component extends Component<Args> {
  @service declare session: Session;
  @service declare router: RouterService;
  @service declare inbox2AssigneeSearch: Inbox2AssigneeSearch;
  @service declare inbox2TagsSearch: Inbox2TagsSearch;
  @service declare userApi: UserApi;
  @service declare companyApi: CompanyApi;
  @service declare intl: IntlService;
  @service declare intercomEventService: any;
  @service declare experimentsApi: ExperimentsApi;
  @service declare conversationsTableSettings: ConversationsTableSettings;
  @service declare inbox2ConversationalInsightsApi: ConversationalInsightsApi;
  @service declare inboxApi: InboxApi;
  @service declare inboxState: InboxState;
  @service declare inboxRedirectionService: InboxRedirectionService;
  @service('filterable-attributes')
  declare filterableAttributesService: FilterableAttributesService;
  @service declare inboxSidebarService: InboxSidebarService;

  @tracked activeSortOption: SortOption = this.initialSortOption();
  private _conversationAttributeDescriptors: (
    | AttributeWithListOptions
    | ConversationAttributeDescriptor
  )[] = [];
  private _ticketTypes: TicketTypeData[] = [];
  @tracked _allFilterItems: MoreFiltersGroup[] = [];
  @tracked selectedFiltersItems: MoreFiltersGroup[] = [];
  @tracked filtersQuery = '';
  @tracked filtersToShowTopLevel: MoreFiltersItem[] = [];
  @tracked visibleMonitorModeFilters: Set<string> = new Set();
  @tracked descriptors: ConversationAttributeDescriptor[] = [];
  @tracked ticketTypes: TicketType[] = [];
  @tracked tableColumns?: ConversationsTableColumns;

  readonly DEFAULT_ROW_HEIGHT_RELEVANT_SEARCH = 155;
  readonly CREATED_BY_TEAMMATE_ATTRUBUTE_IDENTIFIER =
    'conversation.custom_attribute.created_by_teammate_id';

  // Attributes that have dedicated UI components and should be excluded from "more filters" dropdown
  readonly EXCLUDED_FROM_MORE_FILTERS = [
    'conversation.tag_ids',
    'conversation.admin_assignee_id',
    'conversation.team_assignee_id',
    'conversation.priority',
    'conversation.title',
    'conversation.state',
    'user_ids',
    'company_ids',
    'conversation.associated_company_ids',
    'conversation.created_at',
    'conversation.updated_at',
    'conversation.snoozed_until',
    'conversation.topics',
    'conversation.ticket_state',
    'conversation.ticket_type_id',
    'conversation.conversation_started_at',
  ];

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    taskFor(this.loadConversationAndTicketAttributes).perform(owner);

    // Convert monitor URL param to filter predicate
    this.convertMonitorParamToFilter();

    // Sync monitorId to sidebar service for score tab
    this.syncMonitorToSidebarService();

    registerDestructor(this, () => {
      taskFor(this.loadConversationAndTicketAttributes).cancelAll();
      // Clear monitor from sidebar service on destroy
      this.inboxSidebarService.currentMonitorId = null;
    });
  }

  private syncMonitorToSidebarService() {
    this.inboxSidebarService.currentMonitorId = this.monitorId ?? null;
  }

  private convertMonitorParamToFilter() {
    if (
      this.session.workspace.isFeatureEnabled('monitor') &&
      this.args.monitor &&
      !this.monitorPredicateFromFilters
    ) {
      this.predicateGroup.add({
        identifier: 'monitor',
        type: 'string',
        attribute: 'conversation.custom_attribute.qa_monitor_ids',
        comparison: 'eq',
        value: this.args.monitor,
      });
      this.router.replaceWith('inbox.workspace.inbox.search', {
        queryParams: {
          filters: this.predicateGroup.toB64(),
          monitor: null,
          qa_mode: 'true',
        },
      });
    }
  }

  get monitorPredicateFromFilters(): SimplePredicate | undefined {
    return this.predicateGroup.findPredicate<SimplePredicate>('monitor');
  }

  get isQaMode() {
    if (!this.session.workspace.isFeatureEnabled('monitor')) {
      return false;
    }
    return this.args.qaMode === 'true';
  }

  get monitorId(): string | undefined {
    let predicateValue = this.monitorPredicateFromFilters?.value;
    return typeof predicateValue === 'string' ? predicateValue : undefined;
  }

  @cached
  get currentMonitor(): Monitor | undefined {
    if (!this.monitorId || !this.isQaMode) {
      return undefined;
    }
    return this.inboxSidebarService.monitors.find((m) => m.id === this.monitorId);
  }

  get selectedMonitor() {
    return this.currentMonitor;
  }

  get backfillStatus(): string | null | undefined {
    return this.currentMonitor?.backfillStatus;
  }

  get isBackfillProcessing(): boolean {
    let status = this.backfillStatus;
    return status === 'enqueueing' || status === 'in_progress';
  }

  get isBackfillFailed(): boolean {
    return this.backfillStatus === 'failed';
  }

  get isBackfillCompleted(): boolean {
    let status = this.backfillStatus;
    return !status || status === 'completed';
  }

  get shouldShowBackfillState(): boolean {
    if (!this.isQaMode || !this.monitorId) {
      return false;
    }

    if (this.currentMonitor) {
      return !this.isBackfillCompleted;
    }

    // If monitor data isn't loaded yet, use the backfill_status query param as a hint
    let backfillStatusHint = this.args.backfillStatus;
    if (backfillStatusHint) {
      return (
        backfillStatusHint === 'enqueueing' ||
        backfillStatusHint === 'in_progress' ||
        backfillStatusHint === 'failed'
      );
    }

    return false;
  }

  get scorecardEvaluators() {
    return this.selectedMonitor?.scorecard?.evaluators;
  }

  get scorecardDescriptorsIds(): (string | number)[] {
    return this.scorecardEvaluators?.map((e) => parseInt(e.descriptorId, 10)) ?? [];
  }

  get cvdaHeading() {
    return this.intl.t('inbox.search.filters.more-filters.conversation-data-attributes');
  }

  @task
  *loadConversationAndTicketAttributes(owner: unknown): TaskGenerator<void> {
    let [descriptors, ticketTypes] = yield all([
      this.loadConversationAttributeDescriptors(),
      this.loadTicketAttributeDescriptors(),
    ]);

    this.tableColumns = new ConversationsTableColumns(
      owner,
      this.settings.selectedColumns,
      new ColumnDefinitionsBuilder(
        owner,
        ticketTypes,
        descriptors,
        'search',
        this.isQaMode,
        this.scorecardDescriptorsIds,
      ).columnDefinitions,
      ticketTypes,
      [],
      this.scorecardDescriptorsIds ?? [],
    );

    // Only merge withoutCategoryFilteredItems when using legacy (not unified)
    if (
      !this.isStandalone &&
      !this.session.workspace.canUseFeature('team-inbox-unified-filterable-attributes')
    ) {
      this.selectedFiltersItems = this.deepCopyFilters([
        ...this.withoutCategoryFilteredItems,
        ...this.selectedFiltersItems,
      ]);
      this._allFilterItems = [...this.withoutCategoryFilteredItems, ...this._allFilterItems];
    }
    this.loadTopLevelFiltersFromUrl();
  }

  async loadConversationAttributeDescriptors() {
    // Feature flag check - use unified attributes if enabled
    if (this.session.workspace.canUseFeature('team-inbox-unified-filterable-attributes')) {
      try {
        await this.filterableAttributesService.loadForContext('search');

        if (this.filterableAttributesService.isLoadedForContext('search')) {
          // Use pre-formatted filter groups from service (replaces everything)
          let filterItems = this.filterableAttributesService.filterItemsForSearch;

          // Use FilterItem[] directly - it's already compatible with MoreFiltersGroup[]
          this._allFilterItems = filterItems;
          this.selectedFiltersItems = this.deepCopyFilters(this._allFilterItems);

          // Populate _conversationAttributeDescriptors for predicate building
          // Convert Ember Data records back to descriptor-like objects
          // Use getAttributesForContext to get attributes with filtered options for search context
          let allAttributes = this.filterableAttributesService.getAttributesForContext('search');

          // Map to AttributeWithListOptions for consistency with service types
          this._conversationAttributeDescriptors = allAttributes as AttributeWithListOptions[];

          // Fetch monitors and load the monitor filter
          if (this.session.workspace.isFeatureEnabled('monitor')) {
            await this.inboxSidebarService.fetchMonitors();
            this.loadMonitorFilter();
            this.saveQaModeColumnSettings();
          }

          // Return attributes for backward compatibility
          return allAttributes;
        }
      } catch (error) {
        console.error('[Search2] Failed to load unified attributes, falling back:', error);
        // Fall through to legacy implementation
      }
    }

    // Legacy implementation
    let attrs = await this.session.workspace.fetchConversationAttributeDescriptors();
    attrs = attrs.reject((attr) => attr.hideFromInboxSearch);

    this._conversationAttributeDescriptors = attrs.reject(
      (attr) => attr.archived || attr.isFilesType,
    );

    if (this.isStandalone) {
      this._conversationAttributeDescriptors = this._conversationAttributeDescriptors.reject(
        (attr) => attr.id === 'created_by_teammate_id',
      );
    }

    let conversationAttributeDescriptorsWithCategoryConversation =
      this._conversationAttributeDescriptors.filter(
        (attribute) => attribute.category === 'conversation',
      );

    this._allFilterItems = [
      ...this._allFilterItems,
      {
        heading: this.cvdaHeading,
        items: conversationAttributeDescriptorsWithCategoryConversation
          ? conversationAttributeDescriptorsWithCategoryConversation.map((attr) => ({
              attribute: attr,
              groupId: -1,
            }))
          : [],
        id: -1,
      },
    ];

    this.selectedFiltersItems = this.deepCopyFilters(this._allFilterItems);
    return attrs;
  }

  async loadTicketAttributeDescriptors() {
    // Feature flag check - use unified attributes if enabled
    if (
      this.session.workspace.canUseFeature('team-inbox-unified-filterable-attributes') &&
      this.filterableAttributesService.isLoadedForContext('search')
    ) {
      // Ticket attributes already included in filterItemsForSearch
      this.selectedFiltersItems = JSON.parse(JSON.stringify(this._allFilterItems));

      // Populate _ticketTypes for predicate building
      // Use the formatted ticket type data from service
      this._ticketTypes = this.filterableAttributesService.ticketTypeAttributesAsData.map(
        (group) => ({
          ...group,
          descriptors:
            group.descriptors?.map((attr) => ({
              ...attr,
              type: this._getDescriptorTypeFromNormalizedType(attr.type, attr.identifier || ''),
              dataType: this._getDescriptorTypeFromNormalizedType(attr.type, attr.identifier || ''),
            })) || null,
        }),
      );

      // Return ticket type attributes for backward compatibility
      return this._ticketTypes;
    }

    // Legacy implementation
    let ticketTypes = await this.inboxApi.listTicketTypes();

    this._ticketTypes = ticketTypes.filter((ticketType) => !ticketType.archived);

    if (this.isStandalone) {
      this.selectedFiltersItems = JSON.parse(JSON.stringify(this._allFilterItems));
      return ticketTypes;
    }

    this._ticketTypes.forEach((ticketType) => {
      // Legacy path: ticketType.descriptors might have full TicketDescriptor instances with additional properties
      let attrs = ticketType.descriptors?.filter(
        (attr) =>
          !attr.archived &&
          (!('isListedAsAttribute' in attr) || (attr as any).isListedAsAttribute) &&
          (!('isFilesType' in attr) || !(attr as any).isFilesType),
      );

      if (attrs && attrs.length > 0) {
        this._allFilterItems = [
          ...this._allFilterItems,
          {
            heading: ticketType.name,
            items: attrs.map((attr) => ({
              attribute: attr,
              groupId: ticketType.id,
            })),
            id: ticketType.id,
          },
        ];
      }
    });
    this.selectedFiltersItems = JSON.parse(JSON.stringify(this._allFilterItems));

    return ticketTypes;
  }

  private get withoutCategoryFilteredItems() {
    // Skip if using unified attributes (already included in filterItemsForSearch)
    if (
      this.session.workspace.canUseFeature('team-inbox-unified-filterable-attributes') &&
      this.filterableAttributesService.isLoadedForContext('search')
    ) {
      return [];
    }

    // Legacy implementation
    let systemAttributesWithoutCategory = this._conversationAttributeDescriptors.filter(
      (attribute) => !attribute.category,
    );

    let filterItems = [
      {
        items: systemAttributesWithoutCategory
          ? systemAttributesWithoutCategory.map((attr) => ({
              attribute: attr,
              groupId: -2,
            }))
          : [],
        id: -2,
      },
    ];

    return filterItems;
  }

  private deepCopyFilters(filters: MoreFiltersGroup[]) {
    return JSON.parse(JSON.stringify(filters));
  }

  private loadTopLevelFiltersFromUrl() {
    if (this.cvdaPredicate && this.cvdaPredicate.predicates.length > 0) {
      this.filtersToShowTopLevel = this.cvdaPredicate.predicates.reduce<MoreFiltersItem[]>(
        (filters, predicate) => {
          let conversationAttribute = this.getConversationAttributeFromPredicate(predicate);
          let ticketAttribute = this.getTicketAttributeFromPredicate(predicate);

          // Id is unique among conversation or ticket attributes. So we will have either a conversation attribute or ticket attribute in the predicate
          let conversationOrTicketAttribute = conversationAttribute || ticketAttribute;

          if (conversationOrTicketAttribute) {
            // Push the attribute to top level filters
            filters.push({
              attribute: conversationOrTicketAttribute.attribute,
              value: predicate.value || undefined,
              comparison: predicate.comparison,
              groupId: conversationOrTicketAttribute.groupId,
            });
            // Remove the attribute from More filters dropdown
            this.selectedFiltersItems = this.selectedFiltersItems.map((group) => {
              if (group.id === conversationOrTicketAttribute.groupId) {
                return {
                  heading: group.heading,
                  items: group.items.filter(
                    (item) => item.attribute.id !== conversationOrTicketAttribute.attribute.id,
                  ),
                  id: conversationOrTicketAttribute.groupId,
                };
              }
              return group;
            });
          }
          return filters;
        },
        [],
      );
    }

    // After loading URL filters, apply the exclusion logic
    this.selectedFiltersItems = this.getFiltersExcludingTopLevel();
  }

  private getConversationAttributeFromPredicate(
    predicate: Omit<SimplePredicate, 'identifier'>,
  ):
    | { attribute: AttributeWithListOptions | ConversationAttributeDescriptor; groupId: number }
    | undefined {
    let attribute = this._conversationAttributeDescriptors?.find((attr) => {
      return (
        attr.id.toString() === predicate.attribute.replace('conversation.custom_attribute.', '')
      );
    });
    if (!attribute) {
      return undefined;
    }

    // Determine the correct groupId based on whether the attribute has a category
    // Attributes with category 'conversation' belong to group -1
    // Attributes without a category belong to group -2
    let groupId = attribute.category === 'conversation' ? -1 : -2;

    return { attribute, groupId };
  }

  private getTicketAttributeFromPredicate(
    predicate: Omit<SimplePredicate, 'identifier'>,
  ): { attribute: TicketDescriptorData; groupId: number } | undefined {
    let attribute = this._ticketTypes
      // Get descriptors array from each ticket type
      .map((ticketType) => ticketType.descriptors || [])
      // Flatten the array of descriptor array into 1D array
      .flatMap((descriptors) => descriptors)
      // Find the attribute from predicate
      .find((attr) => {
        return (
          attr.id.toString() === predicate.attribute.replace('conversation.custom_attribute.', '')
        );
      });
    return attribute ? { attribute, groupId: attribute.ticketTypeId } : undefined;
  }

  get showMoreFiltersPlaceholder() {
    return taskFor(this.loadConversationAndTicketAttributes).isRunning;
  }

  private getFiltersExcludingTopLevel() {
    return this._allFilterItems.map((group) => {
      return {
        heading: group.heading,
        items: group.items.filter((item) => {
          // Filter out items already in top-level filters
          let isInTopLevel = this.filtersToShowTopLevel.find(
            (filter) => filter.attribute.id === item.attribute.id,
          );

          // Filter out attributes with dedicated UI components
          let identifier = 'identifier' in item.attribute ? item.attribute.identifier : undefined;
          let isExcluded = identifier && this.EXCLUDED_FROM_MORE_FILTERS.includes(identifier);

          // In QA mode, exclude monitor filter since it has a dedicated component
          let isMonitorInQaMode = this.isQaMode && item.attribute.id === 'monitor';

          return !isInTopLevel && !isExcluded && !isMonitorInQaMode;
        }),
        id: group.id,
      };
    });
  }

  @action onFiltersQueryChanged(query: string) {
    this.filtersQuery = query;
    if (query) {
      let lowerCaseQuery = query.toLowerCase();
      this.selectedFiltersItems = this.getFiltersExcludingTopLevel().map((group) => {
        return {
          heading: group.heading,
          items: group.items.filter((item) => {
            return item.attribute.name.toLowerCase().includes(lowerCaseQuery);
          }),
          id: group.id,
        };
      });
    } else {
      this.selectedFiltersItems = this.getFiltersExcludingTopLevel();
    }
  }

  @action selectFilter(item: MoreFiltersItem) {
    this.filtersQuery = '';
    // Add item without a value so the dropdown opens automatically
    this.filtersToShowTopLevel = [...this.filtersToShowTopLevel, { ...item, value: undefined }];
    this.selectedFiltersItems = this.getFiltersExcludingTopLevel().map((group) => {
      return {
        heading: group.heading,
        items: group.items.filter((i) => i.attribute.id !== item.attribute.id),
        id: group.id,
      };
    });
  }

  @action onMoreFilterItemValueUpdated(
    item: MoreFiltersItem,
    value: string | BooleanComparison | CvdaDateTimeItem['value'],
    comparison: Comparison = 'eq',
  ) {
    // Use identifier directly when filterable attributes feature flag is enabled
    // Otherwise, construct it for backward compatibility with legacy system
    let attributeIdentifier = this.session.workspace.canUseFeature(
      'team-inbox-unified-filterable-attributes',
    )
      ? (item.attribute as AttributeWithListOptions).identifier
      : `conversation.custom_attribute.${item.attribute.id}`;

    let predicate: NestedPredicate['predicates'][number] = {
      attribute: attributeIdentifier,
      comparison: item.attribute.type === 'boolean' ? (value as BooleanComparison) : comparison,
      type: this.getPredicateType(item.attribute),
      value,
    };

    if (this.cvdaPredicate) {
      this.cvdaPredicate.predicates = this.cvdaPredicate.predicates.filter(
        (predicate) => predicate.attribute !== attributeIdentifier,
      );
      this.cvdaPredicate.predicates.pushObject(predicate);
    } else {
      this.predicateGroup.add({
        identifier: 'conversation-attribute',
        type: 'and',
        predicates: [predicate],
      });
    }

    this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });
  }

  private getPredicateType(
    item:
      | AttributeWithListOptions
      | TicketDescriptorData
      | ConversationAttributeDescriptor
      | TicketDescriptor,
  ): DataType | 'conversation_attribute_list' | 'float' | 'string' {
    // Handle both string types (from AttributeWithListOptions) and DataType enum (from ConversationAttributeDescriptor)
    let type = item.type;
    if (type === DataType.List || type === 'list') {
      return 'conversation_attribute_list';
    } else if (type === DataType.Decimal || type === 'decimal') {
      return 'float';
    }
    return type as DataType;
  }

  @action onMoreFilterItemRemoved(item: MoreFiltersItem) {
    this.filtersToShowTopLevel = this.filtersToShowTopLevel.filter(
      (i) => i.attribute.id !== item.attribute.id,
    );

    item.value = undefined;

    this.selectedFiltersItems = this.selectedFiltersItems.map((group) => {
      if (group.id === item.groupId) {
        return {
          heading: group.heading,
          items: [...group.items, item],
          id: group.id,
        };
      }
      return group;
    });

    if (this.cvdaPredicate) {
      // Use identifier directly when filterable attributes feature flag is enabled
      // Otherwise, construct it for backward compatibility with legacy system
      let attributeIdentifier = this.session.workspace.canUseFeature(
        'team-inbox-unified-filterable-attributes',
      )
        ? (item.attribute as AttributeWithListOptions).identifier
        : `conversation.custom_attribute.${item.attribute.id}`;

      this.cvdaPredicate.predicates = this.cvdaPredicate.predicates.filter(
        (predicate) => predicate.attribute !== attributeIdentifier,
      );
      if (this.cvdaPredicate.predicates.length === 0) {
        this.predicateGroup.remove('conversation-attribute');
      }
    }
    this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });
  }

  private clearMoreFilters() {
    this.filtersToShowTopLevel = [];
    this.selectedFiltersItems = this.getFiltersExcludingTopLevel();
  }

  initialSortOption(): SortOption {
    let fallbackSortOption = this.relevanceSortOption;
    let selectedSortOption;
    if (this.args.sort) {
      selectedSortOption = this.sortOptions.find((option) => option.id === this.args.sort);
      this.saveSortSettings(selectedSortOption);
    } else if (this.settings.selectedSortOption) {
      selectedSortOption = this.sortOptions.find((option) =>
        isEqual(option.sortState, this.settings.selectedSortOption),
      );
    }
    return selectedSortOption || fallbackSortOption;
  }

  get hasConversationOrTicketAttribute() {
    return this._allFilterItems.some((group) => group.items.length > 0);
  }

  get canShowMoreFilters() {
    return this.hasConversationOrTicketAttribute;
  }

  get standardFiltersForMonitorMode(): MoreFiltersGroup[] {
    let standardFilters = [
      {
        id: 'teammate',
        name: this.intl.t('inbox.search.filters.teammate.opener-blank'),
        icon: 'person',
      },
      { id: 'team', name: this.intl.t('inbox.search.filters.team.opener-blank'), icon: 'team' },
      { id: 'tag', name: this.intl.t('inbox.search.filters.tag.opener-blank'), icon: 'tag' },
      { id: 'user', name: this.intl.t('inbox.search.filters.user.opener-blank'), icon: 'person' },
      {
        id: 'company',
        name: this.intl.t('inbox.search.filters.company.opener-blank'),
        icon: 'company',
      },
      { id: 'date', name: this.intl.t('inbox.search.filters.date.opener-blank'), icon: 'calendar' },
      { id: 'state', name: this.intl.t('inbox.search.filters.status.none'), icon: 'inbox' },
    ];

    let availableFilters = standardFilters.filter((f) => !this.visibleMonitorModeFilters.has(f.id));

    if (availableFilters.length === 0) {
      return [];
    }

    return [
      {
        heading: undefined,
        items: availableFilters.map((filter) => ({
          attribute: {
            id: `standard-filter-${filter.id}`,
            identifier: `standard-filter-${filter.id}`,
            name: filter.name,
            icon: filter.icon,
            type: 'standard',
          } as unknown as ConversationAttributeDescriptor,
          groupId: -100,
        })),
        id: -100,
      },
    ];
  }

  get monitorModeFiltersItems(): MoreFiltersGroup[] {
    return [...this.standardFiltersForMonitorMode, ...this.selectedFiltersItems];
  }

  @action selectMonitorModeFilter(item: MoreFiltersItem) {
    let identifier = String(
      (item.attribute as { identifier?: string }).identifier || item.attribute.id,
    );

    if (identifier.startsWith('standard-filter-')) {
      let filterType = identifier.replace('standard-filter-', '');
      this.visibleMonitorModeFilters = new Set([...this.visibleMonitorModeFilters, filterType]);
      return;
    }

    this.selectFilter(item);
  }

  isFilterVisible = (filterId: string): boolean => {
    return this.visibleMonitorModeFilters.has(filterId);
  };

  @action hideMonitorModeFilter(filterId: string) {
    let newSet = new Set(this.visibleMonitorModeFilters);
    newSet.delete(filterId);
    this.visibleMonitorModeFilters = newSet;
  }

  @action selectAndMaybeHideFilter(
    filterType: string,
    originalAction: (value: unknown) => void,
    value: unknown,
  ) {
    originalAction(value);
    if (!value && this.isQaMode) {
      this.hideMonitorModeFilter(filterType);
    }
  }

  @cached
  get predicateGroup() {
    return PredicateGroup.from(this.args.filters ?? '');
  }

  @cached
  get teammatePredicate() {
    return this.predicateGroup.findPredicate<SimplePredicate>('teammate');
  }

  @cached
  get teamPredicate() {
    return this.predicateGroup.findPredicate<NestedPredicate>('team');
  }

  @cached
  get createdByPredicate(): Omit<SimplePredicate, 'identifier'> | undefined {
    let cvdaPredicates = this.cvdaPredicate?.predicates;

    if (cvdaPredicates && cvdaPredicates.length > 0) {
      return cvdaPredicates.find(
        (predicate) => predicate.attribute === this.CREATED_BY_TEAMMATE_ATTRUBUTE_IDENTIFIER,
      );
    }
    return;
  }

  @cached
  get tagsPredicate() {
    return this.predicateGroup.findPredicate<NestedPredicate>('tags');
  }

  @cached
  get userPredicate() {
    return this.predicateGroup.findPredicate<SimplePredicate>('user');
  }

  @cached
  get companyPredicate() {
    return this.predicateGroup.findPredicate<SimplePredicate>('company');
  }

  @cached
  get dateRangeStartPredicate() {
    return this.predicateGroup.findPredicate<DateRangePredicate>('date-range-start');
  }

  @cached
  get dateRangeEndPredicate() {
    return this.predicateGroup.findPredicate<DateRangePredicate>('date-range-end');
  }

  @cached
  get statePredicate() {
    return this.predicateGroup.findPredicate<SimplePredicate>('state');
  }

  @cached
  get priorityPredicate() {
    return this.predicateGroup.findPredicate<SimplePredicate>('priority');
  }

  @cached
  get conversationTypePredicate() {
    return this.predicateGroup.findPredicate<SimplePredicate>('conversation-type');
  }

  @cached
  get ticketStatePredicate() {
    return this.predicateGroup.findPredicate<TicketStatePredicate>('ticket-state');
  }

  @cached
  get topicPredicate() {
    return this.predicateGroup.findPredicate<NestedPredicate>('conversation-topic');
  }

  @cached get cvdaPredicate() {
    return this.predicateGroup.findPredicate<NestedPredicate>('conversation-attribute');
  }

  @cached
  get titlePredicate() {
    return this.predicateGroup.findPredicate<SimplePredicate>('conversation-title');
  }

  @cached
  get qaStatusPredicate() {
    if (!this.cvdaPredicate) {
      return undefined;
    }
    // QA status is stored as a nested predicate within conversation-attribute
    return this.cvdaPredicate.predicates.find(
      (predicate) => predicate.attribute === 'conversation.custom_attribute.qa_status',
    );
  }

  @cached
  get qaAssigneePredicate() {
    if (!this.cvdaPredicate) {
      return undefined;
    }
    // QA assignee is stored as a nested predicate within conversation-attribute
    return this.cvdaPredicate.predicates.find(
      (predicate) => predicate.attribute === 'conversation.custom_attribute.qa_assignee_id',
    );
  }

  @cached
  get qaMonitorAssigneesPredicate() {
    if (!this.cvdaPredicate) {
      return undefined;
    }
    return this.cvdaPredicate.predicates.find(
      (predicate) => predicate.attribute === 'conversation.custom_attribute.qa_monitor_assignees',
    );
  }

  @cached
  get qaMonitorStatusesPredicate() {
    if (!this.cvdaPredicate) {
      return undefined;
    }
    return this.cvdaPredicate.predicates.find(
      (predicate) => predicate.attribute === 'conversation.custom_attribute.qa_monitor_statuses',
    );
  }

  get sortOptions(): readonly SortOption[] {
    return Object.freeze([
      this.relevanceSortOption,
      {
        id: InboxSearchSort.Newest,
        text: this.intl.t(`inbox.conversation-filters.sort-by.newest`),
        sortState: { sortField: 'sorting_updated_at', direction: 'desc' },
      },
      {
        id: InboxSearchSort.Oldest,
        text: this.intl.t(`inbox.conversation-filters.sort-by.oldest`),
        sortState: { sortField: 'sorting_updated_at', direction: 'asc' },
      },
    ]);
  }

  get relevanceSortOption(): SortOption {
    return {
      id: InboxSearchSort.Relevance,
      text: this.intl.t(`inbox.conversation-filters.sort-by.relevance`),
      sortState: { sortField: 'relevance', direction: 'desc' },
    };
  }

  get teammateAssigned() {
    return this.inbox2AssigneeSearch.searchableAdmins.find(
      (admin) => admin.data.id.toString() === this.teammatePredicate?.value,
    )?.data as AdminWithPermissions | undefined;
  }

  get isStandalone() {
    return this.session.workspace.hasLightStandaloneInbox;
  }

  // Convert normalized types to descriptor types (handles special cases)
  // Attributes now store normalized types, so we only need to handle special identifier-based cases
  private _getDescriptorTypeFromNormalizedType(normalizedType: string, identifier: string): string {
    // Special identifier-based transformations
    if (identifier === 'conversation.tag_ids') {
      return 'manual_tag';
    }
    if (identifier === 'conversation.ticket_state') {
      return 'ticket_state';
    }
    // Normalized types pass through directly (e.g., 'list' stays 'list')
    return normalizedType;
  }

  private loadMonitorFilter() {
    let scorecardGroup = this._allFilterItems.find((g) => g.id === -3);
    let monitorItem = scorecardGroup?.items.find((i) => i.attribute.id === 'monitor');
    if (!monitorItem) {
      return;
    }

    let updatedAttribute = {
      ...monitorItem.attribute,
      listOptions: this.inboxSidebarService.monitors.map((m) => ({
        id: m.id,
        label: m.name,
        archived: m.archived || false,
      })),
    } as AttributeWithListOptions;

    this._allFilterItems = this.updateMonitorAttributeInGroup(
      this._allFilterItems,
      updatedAttribute,
    );

    this.selectedFiltersItems = this.getFiltersExcludingTopLevel();
  }

  private saveQaModeColumnSettings() {
    if (!this.isQaMode) {
      return;
    }

    let activityColumns = [
      { id: 'contact', width: 256 },
      { id: 'activity', width: 190 },
      { id: 'lastUpdated', width: 128 },
    ];

    let scorecardColumns = (this.scorecardEvaluators ?? []).map((evaluator) => ({
      id: `attribute_${evaluator.descriptorId}`,
      width: 160,
    }));

    let qaColumns = [
      { id: 'customScore', width: 140 },
      { id: 'qaAssignee', width: 200 },
      { id: 'qaStatus', width: 200 },
    ];

    this.saveColumnSettings([...activityColumns, ...scorecardColumns, ...qaColumns]);
  }

  private updateMonitorAttributeInGroup(
    groups: MoreFiltersGroup[],
    updatedAttribute: AttributeWithListOptions,
  ): MoreFiltersGroup[] {
    return groups.map((group) =>
      group.id !== -3
        ? group
        : {
            ...group,
            items: group.items.map((item: MoreFiltersItem) =>
              item.attribute.id === 'monitor' ? { ...item, attribute: updatedAttribute } : item,
            ),
          },
    );
  }

  @action toggleConversationList() {
    this.inboxState.set('isConversationListHidden', !this.inboxState.isConversationListHidden);
  }

  get teamsAssigned() {
    let teams = new Set(this.teamPredicate?.predicates.map((predicate) => predicate.value));

    return (this.inbox2AssigneeSearch.searchableTeams
      .filter((team) => teams.has(team.data.id.toString()))
      ?.map((row) => row.data) ?? []) as TeamSummary[];
  }

  get dateRange() {
    if (this.dateRangeStartPredicate && this.dateRangeEndPredicate) {
      return {
        start: fromAbsoluteDate(this.dateRangeStartPredicate.value),
        end: fromAbsoluteDate(this.dateRangeEndPredicate.value),
      };
    } else {
      return undefined;
    }
  }

  get tags() {
    if (!this.tagsPredicate) {
      return [];
    }

    let ids = new Set(this.tagsPredicate.predicates.map((predicate) => predicate.value));
    return (this.inbox2TagsSearch.searchableTags || [])
      .filter((tag) => ids.has(tag.data.id.toString()))
      .map((tag) => tag.data);
  }

  get state() {
    return intToState(this.statePredicate?.value);
  }

  get priority() {
    return this.priorityPredicate?.comparison as BooleanComparison;
  }

  get ticketState() {
    return this.ticketStatePredicate?.value;
  }

  get hasParams(): boolean {
    return !isEmpty(this.args.query?.trim()) || !this.predicateGroup.isEmpty;
  }

  get conversationType() {
    let comparison = this.conversationTypePredicate?.comparison;
    if (comparison === 'unknown') {
      return InboxConversationTypeOption.Conversation;
    } else if (comparison === 'known') {
      return InboxConversationTypeOption.Ticket;
    }
    return InboxConversationTypeOption.All;
  }

  get title() {
    return (this.titlePredicate?.value as string) || '';
  }

  get qaStatus() {
    if (
      this.session.workspace.isFeatureEnabled('fin-qa-multiple-monitor-assignees') &&
      this.monitorId
    ) {
      let perMonitorValue = this.qaMonitorStatusesPredicate?.value as string | undefined;
      if (perMonitorValue) {
        return getValueFromMonitorPairs(perMonitorValue, this.monitorId) ?? undefined;
      }
      return undefined;
    }
    return this.qaStatusPredicate?.value as string | undefined;
  }

  get qaStatusOptions() {
    // Find the qa_status filter from the processed filter items
    let qaStatusFilter = this._allFilterItems
      .flatMap((group) => group.items)
      .find((item) => item.attribute.id === 'qa_status');

    if (!qaStatusFilter) {
      return [];
    }

    // Return the list options from the attribute
    return qaStatusFilter.attribute.listOptions || [];
  }

  get qaAssignee() {
    let adminId: string | undefined;

    if (
      this.session.workspace.isFeatureEnabled('fin-qa-multiple-monitor-assignees') &&
      this.monitorId
    ) {
      let perMonitorValue = this.qaMonitorAssigneesPredicate?.value as string | undefined;
      if (perMonitorValue) {
        adminId = getValueFromMonitorPairs(perMonitorValue, this.monitorId) ?? undefined;
      }
    } else {
      adminId = this.qaAssigneePredicate?.value as string | undefined;
    }

    if (adminId) {
      return this.inbox2AssigneeSearch.searchableAdmins.find(
        (admin) => admin.data.id.toString() === adminId,
      )?.data as AdminWithPermissions | undefined;
    }
    return undefined;
  }

  private isFilterInTopLevel(attributeId: string): boolean {
    return this.filtersToShowTopLevel.some((filter) => filter.attribute.id === attributeId);
  }

  private updateConversationAttribute(attributeName: string, value: string | null): void {
    let attribute = `conversation.custom_attribute.${attributeName}`;

    if (this.cvdaPredicate) {
      this.cvdaPredicate.predicates = this.cvdaPredicate.predicates.filter(
        (predicate) => predicate.attribute !== attribute,
      );

      if (value) {
        this.cvdaPredicate.predicates.pushObject({
          attribute,
          comparison: 'eq',
          type: 'conversation_attribute_list',
          value,
        });
      }

      // Remove empty group to keep predicate structure clean
      if (this.cvdaPredicate.predicates.length === 0) {
        this.predicateGroup.remove('conversation-attribute');
      }
    } else if (value) {
      this.predicateGroup.add({
        identifier: 'conversation-attribute',
        type: 'and',
        predicates: [
          {
            attribute,
            comparison: 'eq',
            type: 'conversation_attribute_list',
            value,
          },
        ],
      });
    }
  }

  private removeFilterFromTopLevel(attributeId: string): void {
    let removedFilter = this.filtersToShowTopLevel.find(
      (filter) => filter.attribute.id === attributeId,
    );

    this.filtersToShowTopLevel = this.filtersToShowTopLevel.filter(
      (filter) => filter.attribute.id !== attributeId,
    );

    // Add back to selectedFiltersItems dropdown
    if (removedFilter) {
      this.selectedFiltersItems = this.selectedFiltersItems.map((group) => {
        if (group.id === removedFilter.groupId) {
          return {
            heading: group.heading,
            items: [...group.items, removedFilter],
            id: group.id,
          };
        }
        return group;
      });
    }
  }

  get showQaStatusFilter() {
    // Only show if we have options available (service is loaded)
    if (this.qaStatusOptions.length === 0) {
      return false;
    }
    // Show if it has a value OR if it's been added from More Filters
    return !!this.qaStatus || this.isFilterInTopLevel('qa_status');
  }

  get showQaAssigneeFilter() {
    // Show if it has a value OR if it's been added from More Filters
    return !!this.qaAssignee || this.isFilterInTopLevel('qa_assignee_id');
  }

  get showMonitorFilter() {
    if (this.inboxSidebarService.monitors.length === 0) {
      return false;
    }
    return !!this.monitorId || this.isFilterInTopLevel('monitor');
  }

  get filtersForMoreFiltersItem() {
    return this.filtersToShowTopLevel.filter((filter) => {
      if (filter.attribute.id === 'qa_status' || filter.attribute.id === 'qa_assignee_id') {
        return false;
      }
      if (filter.attribute.id === 'monitor') {
        return false;
      }
      return true;
    });
  }

  @use topicsLoader = AsyncData<ConversationTopic[]>(async ({ signal }) => {
    let topics = await this.inbox2ConversationalInsightsApi.fetchAllTopics({ signal });
    return topics;
  });

  @restartableTask *onQueryChanged(value: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'search_initiated',
      object: 'conversations',
      query_keyword: value,
      section: 'search',
      predicates: JSON.stringify(this.predicateGroup),
    });

    yield timeout(ENV.APP._350MS);

    this.transitionToSearchHome({ query: value });
  }

  @action clearFilters() {
    this.clearMoreFilters();
    this.transitionToSearchHome({ filters: undefined, sort: undefined, query: undefined });
  }

  @action transitionToSearchHome(queryParams?: Record<string, string | undefined>) {
    if (queryParams) {
      this.router.transitionTo('inbox.workspace.inbox.search', {
        queryParams,
      });
    } else {
      this.router.transitionTo('inbox.workspace.inbox.search');
    }
  }

  @action selectTeammate(admin: AdminSummary | undefined) {
    let adminId = convertStringIdToNumber(this.teammatePredicate?.value as string);
    let adminAlreadyAssigned = adminId === admin?.id;

    if (!admin || adminAlreadyAssigned) {
      this.predicateGroup.remove('teammate');
    } else if (this.teammatePredicate) {
      this.teammatePredicate.value = admin.id.toString();
    } else {
      this.predicateGroup.add({
        identifier: 'teammate',
        attribute: 'conversation.admin_assignee_id',
        comparison: 'eq',
        type: 'admin_assignee_id',
        value: admin.id.toString(),
      });
    }

    this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });
  }

  @action selectMonitor(monitorId: string | null) {
    if (!monitorId) {
      this.predicateGroup.remove('monitor');
    } else if (this.monitorPredicateFromFilters) {
      this.monitorPredicateFromFilters.value = monitorId;
    } else {
      this.predicateGroup.add({
        identifier: 'monitor',
        type: 'string',
        attribute: 'conversation.custom_attribute.qa_monitor_ids',
        comparison: 'eq',
        value: monitorId,
      });
    }

    // When switching monitors, clear per-monitor QA filters so stale
    // predicates from the previous monitor don't persist in search results
    if (this.session.workspace.isFeatureEnabled('fin-qa-multiple-monitor-assignees')) {
      this.updateConversationAttribute('qa_monitor_statuses', null);
      this.removeFilterFromTopLevel('qa_monitor_statuses');
      this.updateConversationAttribute('qa_monitor_assignees', null);
      this.removeFilterFromTopLevel('qa_monitor_assignees');
    }

    // Sync to sidebar service for score tab to use
    this.inboxSidebarService.currentMonitorId = monitorId;

    this.transitionToSearchHome({
      filters: this.predicateGroup.toB64(),
      qa_mode: this.args.qaMode,
    });
  }

  @action selectTeam(team: TeamSummary | null) {
    if (!team) {
      this.predicateGroup.remove('team');
      this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });

      return;
    }

    let predicate: NestedPredicate['predicates'][number] = {
      attribute: 'conversation.team_assignee_id',
      comparison: 'eq',
      type: 'team_assignee_id',
      value: team.id.toString(),
    };

    if (this.teamPredicate) {
      if (this.teamsAssigned?.any((t) => team.id === t.id)) {
        this.teamPredicate.predicates = this.teamPredicate.predicates.filter(
          (predicate) => predicate.value !== team.id.toString(),
        );

        if (this.teamPredicate.predicates.length === 0) {
          this.predicateGroup.remove('team');
        }
      } else {
        this.teamPredicate.predicates.pushObject(predicate);
      }
    } else {
      this.predicateGroup.add({
        identifier: 'team',
        type: 'or',
        predicates: [predicate],
      });
    }

    this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });
  }

  @action selectTag(tag: Tag) {
    let predicate: NestedPredicate['predicates'][number] = {
      attribute: 'conversation.tag_ids',
      comparison: 'in',
      type: 'manual_tag',
      value: tag.id.toString(),
    };

    if (this.tagsPredicate) {
      if (this.tags.any((t) => tag.id === t.id)) {
        this.tagsPredicate.predicates = this.tagsPredicate.predicates.filter(
          (predicate) => predicate.value !== tag.id.toString(),
        );

        if (this.tagsPredicate.predicates.length === 0) {
          this.clearTags();
        }
      } else {
        this.tagsPredicate.predicates.pushObject(predicate);
      }
    } else {
      this.predicateGroup.add({
        identifier: 'tags',
        type: 'and',
        predicates: [predicate],
      });
    }

    this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });
  }

  @action clearTags() {
    this.predicateGroup.remove('tags');
    this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });
  }

  @action selectTopic(topic: ConversationTopic) {
    let predicate: NestedPredicate['predicates'][number] = {
      attribute: 'conversation.topics',
      comparison: 'in',
      type: 'conversation_topic',
      value: topic.id.toString(),
    };

    if (this.topicPredicate) {
      if (this.topics.any((t) => topic.id === t.id)) {
        this.topicPredicate.predicates = this.topicPredicate.predicates.filter(
          (predicate) => predicate.value !== topic.id.toString(),
        );

        if (this.topicPredicate.predicates.length === 0) {
          this.clearTopics();
        }
      } else {
        this.topicPredicate.predicates.pushObject(predicate);
      }
    } else {
      this.predicateGroup.add({
        identifier: 'conversation-topic',
        type: 'and',
        predicates: [predicate],
      });
    }

    this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });
  }

  @action clearTopics() {
    this.predicateGroup.remove('conversation-topic');
    this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });
  }

  @action selectUser(user: UserSummary | null) {
    let userAlreadyAssigned = this.userPredicate?.value === user?.id;

    if (!user || userAlreadyAssigned) {
      this.predicateGroup.remove('user');
    } else if (this.userPredicate) {
      this.userPredicate.value = user.id.toString();
    } else {
      this.predicateGroup.add({
        identifier: 'user',
        attribute: 'user_ids',
        comparison: 'eq',
        type: 'id',
        value: user.id.toString(),
      });
    }

    this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });
  }

  @action selectCompany(company: CompanySummary | null) {
    let toggleSelectedCompany = this.companyPredicate?.value === company?.id;

    if (!company || toggleSelectedCompany) {
      this.predicateGroup.remove('company');
    } else if (this.companyPredicate) {
      this.companyPredicate.value = company.id.toString();
    } else {
      this.predicateGroup.add({
        identifier: 'company',
        attribute: 'associated_company_ids',
        comparison: 'eq',
        type: 'id',
        value: company.id.toString(),
      });
    }

    this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });
  }

  @action selectDate(start: moment.Moment | null, end: moment.Moment | null) {
    let isSameDateAlreadySelected =
      start &&
      isEqual(this.dateRangeStartPredicate?.value, toAbsoluteDate(start)) &&
      end &&
      isEqual(this.dateRangeEndPredicate?.value, toAbsoluteDate(end));

    if (!start || !end || isSameDateAlreadySelected) {
      this.predicateGroup.remove('date-range-start');
      this.predicateGroup.remove('date-range-end');
    } else if (this.dateRangeStartPredicate && this.dateRangeEndPredicate) {
      this.dateRangeStartPredicate.value = toAbsoluteDate(start);
      this.dateRangeEndPredicate.value = toAbsoluteDate(end);
    } else {
      this.predicateGroup.add({
        identifier: 'date-range-start',
        attribute: 'conversation_started_at',
        comparison: 'gt',
        type: 'date',
        value: toAbsoluteDate(start),
      });

      this.predicateGroup.add({
        identifier: 'date-range-end',
        attribute: 'conversation_started_at',
        comparison: 'lt',
        type: 'date',
        value: toAbsoluteDate(end),
      });
    }

    this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });
  }

  @action selectConversationType({ id }: { id: InboxConversationTypeOption }) {
    this.predicateGroup.remove('conversation-type');

    if (id === InboxConversationTypeOption.Ticket) {
      this.predicateGroup.add({
        identifier: 'conversation-type',
        attribute: 'conversation.ticket_type_id',
        comparison: 'known',
        type: 'integer',
        value: null,
      });
    } else if (id === InboxConversationTypeOption.Conversation) {
      this.predicateGroup.add({
        identifier: 'conversation-type',
        attribute: 'conversation.ticket_type_id',
        comparison: 'unknown',
        type: 'integer',
        value: null,
      });
    }
    return this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });
  }

  @action selectState(state: InboxStateOption | null) {
    let isSameStateAlreadySelected =
      this.ticketStatePredicate?.value === state ||
      intToState(this.statePredicate?.value) === state;

    if (!state || isSameStateAlreadySelected) {
      this.predicateGroup.remove('state');
      this.predicateGroup.remove('ticket-state');
      return this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });
    }

    if (isTicketState(state)) {
      let ticketState = state as TicketSystemState;
      this.predicateGroup.remove('state');
      if (this.ticketStatePredicate) {
        this.ticketStatePredicate.value = ticketState;
      } else {
        this.predicateGroup.add({
          identifier: 'ticket-state',
          attribute: 'conversation.ticket_state',
          comparison: 'eq',
          type: 'string',
          value: ticketState,
        });
      }
    } else {
      this.predicateGroup.remove('ticket-state');
      let enumValue = stateToInt(state);
      if (enumValue === undefined) {
        throw new Error(`Setting invalid state ${enumValue}`);
      }

      if (this.statePredicate) {
        this.statePredicate.value = enumValue;
      } else {
        this.predicateGroup.add({
          identifier: 'state',
          attribute: 'conversation.state',
          comparison: 'eq',
          type: 'integer',
          value: enumValue,
        });
      }
    }

    this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });
  }

  @action selectPriority(priority: BooleanComparison) {
    this.predicateGroup.remove('priority');

    if (priority !== undefined) {
      this.predicateGroup.add({
        identifier: 'priority',
        type: 'priority',
        attribute: 'conversation.priority',
        comparison: priority,
        value: null,
      });
    }

    return this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });
  }

  @action selectQaStatus(status: string | null) {
    if (
      this.session.workspace.isFeatureEnabled('fin-qa-multiple-monitor-assignees') &&
      this.monitorId &&
      status
    ) {
      this.updateConversationAttribute('qa_monitor_statuses', `${this.monitorId}:${status}`);
    } else {
      this.updateConversationAttribute('qa_status', status);
      if (!status) {
        this.removeFilterFromTopLevel('qa_status');
        if (this.session.workspace.isFeatureEnabled('fin-qa-multiple-monitor-assignees')) {
          this.updateConversationAttribute('qa_monitor_statuses', null);
          this.removeFilterFromTopLevel('qa_monitor_statuses');
        }
      }
    }

    return this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });
  }

  @action selectQaAssignee(admin: AdminWithPermissions | AdminSummary | undefined) {
    if (
      this.session.workspace.isFeatureEnabled('fin-qa-multiple-monitor-assignees') &&
      this.monitorId &&
      admin
    ) {
      this.updateConversationAttribute('qa_monitor_assignees', `${this.monitorId}:${admin.id}`);
    } else {
      this.updateConversationAttribute('qa_assignee_id', admin ? admin.id.toString() : null);
      if (!admin) {
        this.removeFilterFromTopLevel('qa_assignee_id');
        if (this.session.workspace.isFeatureEnabled('fin-qa-multiple-monitor-assignees')) {
          this.updateConversationAttribute('qa_monitor_assignees', null);
          this.removeFilterFromTopLevel('qa_monitor_assignees');
        }
      }
    }

    return this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });
  }

  @action removeQaStatus(event?: Event) {
    event?.stopPropagation();
    this.selectQaStatus(null);
  }

  @action removeQaAssignee(event?: Event) {
    event?.stopPropagation();
    this.selectQaAssignee(undefined);
  }

  @action removeMonitor(event?: Event) {
    event?.stopPropagation();
    this.selectMonitor(null);
    this.removeFilterFromTopLevel('monitor');
  }

  @action selectSort(sort: Search2Component['sortOptions'][number]) {
    this.activeSortOption =
      this.sortOptions.find((option) => option.id === sort.id) || this.relevanceSortOption;
    this.saveSortSettings();
    this.router.transitionTo({
      queryParams: { sort: sort.id },
    });
  }

  get settings() {
    return this.conversationsTableSettings.getSearchSettings(true);
  }

  saveSortSettings(sortOption?: SortOption) {
    this.conversationsTableSettings.saveSearchSettings(
      {
        ...this.settings,
        selectedSortOption: sortOption?.sortState || this.activeSortOption?.sortState,
      },
      true,
    );
  }

  saveColumnSettings(selectedColumns: SelectedColumn[]) {
    this.conversationsTableSettings.saveSearchSettings(
      {
        ...this.settings,
        selectedColumns,
      },
      true,
    );
  }

  @action clearSearch() {
    this.clearMoreFilters();
    this.router.transitionTo('inbox.workspace.inbox.search', {
      queryParams: { filters: null, query: null },
    });
  }

  @action onTableSortChanged(column: ConversationsTableColumn, direction: SortDirection) {
    let option = this.sortOptions.find(
      (option) =>
        option.sortState.sortField === column.sortField && option.sortState.direction === direction,
    );

    if (option) {
      this.activeSortOption = option;
      this.saveSortSettings();

      this.router.transitionTo({
        queryParams: { sort: option.id },
      });
    } else if (column.sortField) {
      this.activeSortOption = {
        id: InboxSearchSort.Attribute,
        text: column.label ?? '',
        sortState: { sortField: column.sortField, direction },
      };
      this.saveSortSettings();
    }
  }

  @action selectTitle(title: string) {
    let titleAlreadyAssigned = this.titlePredicate?.value === title;
    if (titleAlreadyAssigned) {
      return;
    }

    if (!title) {
      this.predicateGroup.remove('conversation-title');
    } else if (this.titlePredicate) {
      this.titlePredicate.value = title;
    } else {
      this.predicateGroup.add({
        identifier: 'conversation-title',
        type: 'title_text_field',
        attribute: 'conversation.title',
        comparison: 'contains',
        value: title,
      });
    }

    this.transitionToSearchHome({ filters: this.predicateGroup.toB64() });
  }

  @use user = AsyncData<UserSummary | undefined>(async ({ signal }) => {
    if (!this.userPredicate) {
      return;
    }

    let user = await this.userApi.fetchUser(this.userPredicate.value as string, { signal });
    return user;
  });

  @use company = AsyncData<CompanySummary | undefined>(async () => {
    if (!this.companyPredicate) {
      return;
    }

    let company = await this.companyApi.fetchCompany(this.companyPredicate.value as string);
    return company;
  });

  get topics() {
    if (!this.topicPredicate) {
      return [];
    }
    let ids = new Set(this.topicPredicate.predicates.map((predicate) => predicate.value));

    return (this.topicsLoader.value ?? []).filter((topic) => ids.has(topic.id.toString()));
  }

  get isSearchTextEmpty(): boolean {
    return !this.args.query || this.args.query === '';
  }

  get hasNoSearchablePredicates(): boolean {
    return (
      this.predicateGroup.isEmpty ||
      (this.predicateGroup.predicates.length === 1 && this.conversationTypePredicate !== undefined)
    );
  }

  get isSearchCriteriaEmpty(): boolean {
    return this.isSearchTextEmpty && this.hasNoSearchablePredicates;
  }

  get fields() {
    if (!this.tableColumns) {
      return [];
    }

    return [
      ...REQUIRED_CONVERSATIONS_TABLE_FIELDS,
      ...this.tableColumns.columns.flatMap((column) => column.fields),
    ].uniq();
  }

  get hiddenColumns() {
    return this.tableColumns?.hiddenColumns ?? [];
  }

  get columns() {
    return this.tableColumns?.columns ?? [];
  }

  @action changeColumns(columns: ColumnDefinition[]) {
    let selectedColumns = columns.map((col) => ({
      id: columnId(col),
      width: col.width,
    }));
    this.tableColumns!.selectedColumns = selectedColumns;
    this.saveColumnSettings(selectedColumns);
  }

  @action onSortColumn(column: ConversationsTableColumn, direction: SortDirection) {
    if (!column.sortField) {
      return;
    }

    this.inboxState.selectedConversations.clear();
    this.onTableSortChanged(column, direction);
  }

  @action onPreviewConversation(
    resource: ConversationsSearchResource,
    conversation: ConversationTableEntry,
  ) {
    if (conversation.relevantPart) {
      let permalink = `part_id=${conversation.relevantPart.generatePermalinkId(conversation.id)}`;
      this.inboxRedirectionService.setConversationPartAnchor(permalink);
    }

    if (this.isQaMode) {
      this.inboxSidebarService.updateActiveTab('score');
    }

    this.router.transitionTo(
      'inbox.workspace.inbox.search.conversation.conversation',
      conversation,
    );

    let conversations = resource.conversations;
    let position = conversations.findIndex(({ id }) => id === conversation.id) + 1;
    let searchUUID = resource.searchUUID;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'opened',
      object: 'conversation',
      section: 'conversation_table',
      conversation_id: conversation.id,
      search_result_position: position,
      search_uuid: searchUUID,
    });
  }

  @action onClosePreview(resource: ConversationsSearchResource) {
    if (this.isQaMode) {
      resource.reload({ skipCache: true, inBackground: false });
    }

    this.router.transitionTo('inbox.workspace.inbox.search', {
      queryParams: { skipRedirection: true },
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Search2': typeof Search2Component;
  }
}
